import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Globe, Ticket } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { shopCards } from "@/components/mall/rooms/banners";
import { useSiteMode } from "@/context/SiteModeContext";

/** The picture half of a card. Services with no photo get a painted panel instead. */
function Visual({ card, ctx }) {
    const src = typeof card.image === "function" ? card.image(ctx) : card.image;
    if (src) return (
        <img src={mediaUrl(src)} alt="" loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
    );
    const Icon = card.key === "website" ? Globe : Ticket;
    return (
        <div className="absolute inset-0 flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${card.accent} 0%, #0B0B0B 140%)` }}>
            <Icon size={64} strokeWidth={1.2} className="text-white/80 transition-transform duration-500 group-hover:scale-110" />
        </div>
    );
}

/**
 * The shop home page: one card per service, click and you are doing something.
 * Big visual, a title and a price or benefit hook — nothing to read, only things to pick.
 */
export function ServiceGrid({ store }) {
    const ctx = useMall();
    const { features } = useSiteMode();
    const cards = shopCards(store, features);
    const q = store?.slug || "";
    return (
        <section id="shop" className="scroll-mt-20 bg-zinc-50" data-testid="service-grid">
            <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-14">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <h2 className="font-display text-2xl font-black tracking-tight text-zinc-900 sm:text-3xl" data-testid="service-grid-title">
                        What do you need printed?
                    </h2>
                    <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">{cards.length} ways to order · pick one</p>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="service-cards">
                    {cards.map((card, i) => (
                        <motion.div key={card.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.3) }}>
                            <Link to={card.to(q)} data-testid={`service-card-${card.key}`} style={{ "--c": card.accent }}
                                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-[color:var(--c)] hover:shadow-[0_24px_40px_-26px_rgba(15,23,42,0.45)]">
                                <div className={`relative aspect-[16/9] overflow-hidden ${card.dark ? "bg-brand-ink" : "bg-zinc-100"}`}>
                                    <Visual card={card} ctx={ctx} />
                                    <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-900 shadow-sm"
                                        data-testid={`service-card-hook-${card.key}`}>
                                        {card.hook}
                                    </span>
                                </div>
                                <div className="flex flex-1 items-start justify-between gap-3 p-4">
                                    <div className="min-w-0">
                                        <h3 className="font-display text-lg font-black leading-tight text-zinc-900" data-testid={`service-card-title-${card.key}`}>{card.title}</h3>
                                        <p className="mt-1 line-clamp-2 text-xs text-zinc-500">{card.line}</p>
                                    </div>
                                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-zinc-200 text-zinc-500 transition-colors duration-300 group-hover:border-[color:var(--c)] group-hover:bg-[color:var(--c)] group-hover:text-white">
                                        <ArrowUpRight size={15} />
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
